// src/features/records/components/RecordErrorBoundary.js
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { logError, ErrorSeverity, ErrorCategory } from '../../../services/errorService';
import mapRecordsErrorToKey from '../utils/recordsErrorMapper';

/**
 * Error boundary component for handling record rendering errors
 * Prevents entire page crashes when individual records fail to render
 */
class RecordErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('Record rendering error:', error, errorInfo);
    
    // Log to analytics for production monitoring
    logError(error, {
      severity: ErrorSeverity.WARNING,
      category: ErrorCategory.RENDERING,
      component: 'RecordErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        recordContext: this.props.recordId || 'unknown'
      }
    });
  }

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      const errorKey = mapRecordsErrorToKey(this.state.error, {
        fallbackKey: 'records.detail.boundary.inline_message'
      });

      // Fallback UI when a record fails to render
      return (
        <div className="record-error-fallback">
          <div style={{
            padding: '20px',
            margin: '12px 0',
            backgroundColor: 'var(--background-elevated)',
            borderRadius: '12px',
            border: '1px solid var(--border-error, #ff4444)',
            color: 'var(--text-secondary)',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0 }}>{t(errorKey)}</p>
            {this.props.showDetails && this.state.error && (
              <details style={{ marginTop: '8px', fontSize: '12px', textAlign: 'left' }}>
                <summary style={{ cursor: 'pointer' }}>
                  {t('records.detail.boundary.details', 'Error details')}
                </summary>
                <pre style={{ 
                  marginTop: '8px', 
                  padding: '8px', 
                  backgroundColor: 'var(--background-base)',
                  borderRadius: '4px',
                  overflow: 'auto'
                }}>
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

RecordErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showDetails: PropTypes.bool,
  recordId: PropTypes.string,
  t: PropTypes.func.isRequired
};

RecordErrorBoundary.defaultProps = {
  showDetails: false
};

export default withTranslation()(RecordErrorBoundary);
